import {Component, OnInit} from '@angular/core';
import {JobApiService, JobConfigResponse} from '@npck/af-frontend-api';
import {LocalizationService, rxSpinner, Spinner} from '@npck/libs/commons';
import {TableEvent} from '../../../components/full-table/full-table.component';
import {INITIAL_PAGE_NUMBER, INITIAL_PAGE_SIZE} from '../../../components/full-table/paging/paginator.component';
import {Column, ColumnType, Row, StatusColumn} from '../../../components/full-table/table/table.component';
import {StatusStyle} from '../../../components/status/status.component';

const CRON_DISABLED = '-';
const CRON_ANY = '*';
const CRON_FIELDS_COUNT = 6;
const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 1440;
const ISO_DURATION_PATTERN = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/;
const CRON_STEP_PATTERN = /^(?:\*|0)\/(\d+)$/;
const CRON_NUMBER_PATTERN = /^\d+$/;
const CRON_HOUR_RANGE_PATTERN = /^(\d+)-(\d+)$/;
const SECOND_KEYS: [string, string, string] = ['i18n_af_jobSecond', 'i18n_af_jobSeconds', 'i18n_af_jobSecondsMany'];
const MINUTE_KEYS: [string, string, string] = ['i18n_af_jobMinute', 'i18n_af_jobMinutes', 'i18n_af_jobMinutesMany'];
const HOUR_KEYS: [string, string, string] = ['i18n_af_jobHour', 'i18n_af_jobHours', 'i18n_af_jobHoursMany'];
const DAY_KEYS: [string, string, string] = ['i18n_af_jobDay', 'i18n_af_jobDays', 'i18n_af_jobDaysMany'];
const WEEKDAY_KEYS: Record<string, string> = {
    MON: 'i18n_af_jobWeekdayMon',
    TUE: 'i18n_af_jobWeekdayTue',
    WED: 'i18n_af_jobWeekdayWed',
    THU: 'i18n_af_jobWeekdayThu',
    FRI: 'i18n_af_jobWeekdayFri',
    SAT: 'i18n_af_jobWeekdaySat',
    SUN: 'i18n_af_jobWeekdaySun',
};
const WEEKDAY_BY_NUMBER = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

@Component({
    selector: 'af-jobs',
    templateUrl: 'jobs.component.html',
    standalone: false,
})
export class JobsComponent implements OnInit {
    Spinner = Spinner;
    dataSource: Row[] = [];
    totalElements = 0;
    private allRows: Row[] = [];
    private pageNumber = INITIAL_PAGE_NUMBER;
    private pageSize = INITIAL_PAGE_SIZE;

    constructor(
        private jobApiService: JobApiService,
        private localizationService: LocalizationService,
    ) {
    }

    ngOnInit(): void {
        this.jobApiService.getJobConfigs()
            .pipe(rxSpinner('jobs'))
            .subscribe(jobs => {
                this.allRows = jobs.map(job => this.buildRow(job));
                this.totalElements = this.allRows.length;
                this.applyPage();
            });
    }

    loadJobs(event: TableEvent): void {
        this.pageNumber = event.page?.pageNumber ?? INITIAL_PAGE_NUMBER;
        this.pageSize = event.page?.pageSize ?? INITIAL_PAGE_SIZE;
        this.applyPage();
    }

    private applyPage(): void {
        const start = this.pageNumber * this.pageSize;
        this.dataSource = this.allRows.slice(start, start + this.pageSize);
    }

    private buildRow(job: JobConfigResponse): Row {
        return <Row>{
            jobName: <Column>{
                type: ColumnType.TEXT,
                text: this.localizationService.getLocalizedKey(`i18n_af_jobName#${job.code}`),
            },
            status: <StatusColumn>{
                type: ColumnType.STATUS,
                text: job.enabled ? 'i18n_af_jobStatusActive' : 'i18n_af_jobStatusPaused',
                style: job.enabled ? StatusStyle.SUCCESS : StatusStyle.INACTIVE,
            },
            jobValue: <Column>{
                type: ColumnType.TEXT,
                text: this.buildValue(job),
            },
        };
    }

    private buildValue(job: JobConfigResponse): string {
        const description = this.localizationService.getLocalizedKey(`i18n_af_jobValue#${job.code}`, {
            expiration: this.formatExpiration(job.expiration),
        });
        return [this.humanizeCron(job.cron), description].filter(Boolean).join('. ');
    }

    private humanizeCron(cron?: string): string {
        if (!cron || cron === CRON_DISABLED) {
            return '';
        }
        const fields = cron.trim().split(/\s+/);
        if (fields.length !== CRON_FIELDS_COUNT) {
            return cron;
        }
        const [second, minute, hour, dayOfMonth, month, dayOfWeek] = fields;
        if (month !== CRON_ANY) {
            return cron;
        }
        if (dayOfWeek !== CRON_ANY) {
            return this.humanizeWeeklyCron(second, minute, hour, dayOfMonth, dayOfWeek) ?? cron;
        }
        const seconds = this.parseCronStep(second);
        if (seconds && dayOfMonth === CRON_ANY && hour === CRON_ANY && minute === CRON_ANY) {
            return this.formatEvery(seconds, SECOND_KEYS, 'i18n_af_jobCronEverySecond');
        }
        if (second !== '0') {
            return cron;
        }
        return this.humanizeMinuteBasedCron(minute, hour, dayOfMonth) ?? cron;
    }

    private humanizeMinuteBasedCron(minute: string, hour: string, dayOfMonth: string): string | null {
        if (dayOfMonth === CRON_ANY && hour === CRON_ANY) {
            if (minute === CRON_ANY) {
                return this.localizationService.getLocalizedKey('i18n_af_jobCronEveryMinute');
            }
            if (minute === '0') {
                return this.localizationService.getLocalizedKey('i18n_af_jobCronEveryHour');
            }
            const minutes = this.parseCronStep(minute);
            return minutes ? this.formatEvery(minutes, MINUTE_KEYS, 'i18n_af_jobCronEveryMinute') : null;
        }
        if (dayOfMonth === CRON_ANY && minute === '0') {
            const hours = this.parseCronStep(hour);
            if (hours) {
                return this.formatEvery(hours, HOUR_KEYS, 'i18n_af_jobCronEveryHour');
            }
        }
        if (dayOfMonth === CRON_ANY && CRON_NUMBER_PATTERN.test(minute)) {
            const hourRange = CRON_HOUR_RANGE_PATTERN.exec(hour);
            if (hourRange) {
                return this.localizationService.getLocalizedKey('i18n_af_jobCronHourlyRange', {
                    from: this.formatTime(hourRange[1], minute),
                    to: this.formatTime(hourRange[2], minute),
                });
            }
        }
        if (!CRON_NUMBER_PATTERN.test(hour) || !CRON_NUMBER_PATTERN.test(minute)) {
            return null;
        }
        const time = this.formatTime(hour, minute);
        if (dayOfMonth === CRON_ANY) {
            return this.localizationService.getLocalizedKey('i18n_af_jobCronDaily', {time});
        }
        return CRON_NUMBER_PATTERN.test(dayOfMonth)
            ? this.localizationService.getLocalizedKey('i18n_af_jobCronMonthly', {day: dayOfMonth, time})
            : null;
    }

    private humanizeWeeklyCron(second: string, minute: string, hour: string, dayOfMonth: string, dayOfWeek: string): string | null {
        if (second !== '0' || dayOfMonth !== CRON_ANY) {
            return null;
        }
        if (!CRON_NUMBER_PATTERN.test(hour) || !CRON_NUMBER_PATTERN.test(minute)) {
            return null;
        }
        const days = this.formatDaysOfWeek(dayOfWeek);
        return days
            ? this.localizationService.getLocalizedKey('i18n_af_jobCronWeekly', {days, time: this.formatTime(hour, minute)})
            : null;
    }

    private formatDaysOfWeek(dayOfWeek: string): string | null {
        const keys = dayOfWeek.split(',').map(day => WEEKDAY_KEYS[this.normalizeWeekday(day)]);
        return keys.every(Boolean)
            ? keys.map(key => this.localizationService.getLocalizedKey(key)).join(', ')
            : null;
    }

    private normalizeWeekday(day: string): string {
        const value = day.trim().toUpperCase();
        return CRON_NUMBER_PATTERN.test(value) ? WEEKDAY_BY_NUMBER[+value] : value;
    }

    private formatTime(hour: string, minute: string): string {
        return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
    }

    private parseCronStep(field: string): number | null {
        const step = CRON_STEP_PATTERN.exec(field);
        return step ? +step[1] : null;
    }

    private formatEvery(value: number, keys: [string, string, string], singleKey: string): string {
        return value === 1
            ? this.localizationService.getLocalizedKey(singleKey)
            : this.localizationService.getLocalizedKey('i18n_af_jobCronEvery', {value: this.pluralize(value, keys)});
    }

    private formatExpiration(expiration?: string): string {
        if (!expiration) {
            return '';
        }
        const parts = ISO_DURATION_PATTERN.exec(expiration);
        if (!parts) {
            return expiration;
        }
        const [, days, hours, minutes] = parts;
        const totalMinutes = (+(days ?? 0) * MINUTES_IN_DAY) + (+(hours ?? 0) * MINUTES_IN_HOUR) + +(minutes ?? 0);
        if (totalMinutes === 0) {
            return expiration;
        }
        if (totalMinutes % MINUTES_IN_DAY === 0) {
            return this.pluralize(totalMinutes / MINUTES_IN_DAY, DAY_KEYS);
        }
        if (totalMinutes % MINUTES_IN_HOUR === 0) {
            return this.pluralize(totalMinutes / MINUTES_IN_HOUR, HOUR_KEYS);
        }
        return this.pluralize(totalMinutes, MINUTE_KEYS);
    }

    private pluralize(value: number, keys: [string, string, string]): string {
        const lastTwoDigits = value % 100;
        const lastDigit = value % 10;
        const key = (lastTwoDigits > 10 && lastTwoDigits < 20) || lastDigit === 0 || lastDigit > 4
            ? keys[2]
            : lastDigit === 1 ? keys[0] : keys[1];
        return `${value} ${this.localizationService.getLocalizedKey(key)}`;
    }
}
